import React from "react"; 
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Heart, Users, Bell, Calendar, Sparkles, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const LandingPage = () => {
  const features = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Track Your Calls",
      description: "Never forget when you last connected. See at a glance who needs a call."
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Stay on a Rhythm",
      description: "Set a simple reach-out rhythm for each person — weekly, monthly, or custom."

    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Build Streaks",
      description: "Watch your relationship streaks grow. Stay motivated to keep in touch."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Organize Contacts",
      description: "Group your people by family, friends, work, or any category you create."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
     title: "Remember the Details",
     description: "Add quick notes after you talk so you don’t forget what mattered last time."

    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Celebrate Connections",
      description: "Animated celebrations when you maintain your streaks. Make calling fun!"
    }
  ];

  const testimonials = [
  {
    quote: "I’m the type to think about people at 2am and feel guilty. This makes it easier to follow through without it becoming a whole “thing.”",
    label: "Beta user"
  },
  {
    quote: "I like that it’s simple. I added a few people I kept forgetting, set a rhythm, and now I don’t have to keep it all in my head.",
    label: "Beta user"
  },
  {
    quote: "The biggest win is the notes. I forget what we talked about — this helps me pick up where we left off.",
    label: "Beta user"
  }
];


  return (
    <div className="min-h-screen bg-[#FDFBF7] noise-texture overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-lg border-b border-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-bold text-lg sm:text-xl text-[#3D405B] hidden min-[400px]:inline" style={{ fontFamily: 'Fraunces, serif' }}>OuttaTouch</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/pricing" data-testid="pricing-link" className="hidden sm:block">
              <Button variant="ghost" className="text-[#3D405B] hover:bg-[#F4F1EA]">
                Pricing
              </Button>
            </Link>
            <Link to="/login" data-testid="login-link">
              <Button variant="ghost" className="text-[#3D405B] hover:bg-[#F4F1EA] px-2 sm:px-4">
                Log In
              </Button>
            </Link>
            <Link to="/register" data-testid="signup-link">
              <Button className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-4 sm:px-6 text-sm sm:text-base">
                <span className="hidden sm:inline">Get Started</span>
                <span className="sm:hidden">Start</span>
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative">
        {/* Background blobs */}
        <div className="blob-1 -top-20 -left-40 opacity-50"></div>
        <div className="blob-2 top-40 -right-20 opacity-40"></div>
        
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 
  className="text-5xl md:text-6xl lg:text-7xl leading-tight text-[#3D405B] mb-6"
  style={{ fontFamily: 'Fraunces, serif' }}
>
  Stay close <span className="doodle-underline">without the guilt</span>.
</h1>

<p className="text-lg md:text-xl text-[#6D6F7C] mb-8 leading-relaxed">
  OuttaTouch helps you remember the people you care about — and reach out before “it’s been too long.”
</p>
            <div className="flex flex-col sm:flex-row gap-4">
  <Link to="/register" data-testid="hero-cta">
    <Button className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
  <span className="hidden sm:inline">Add 3 people you don’t want to lose touch with</span>
  <span className="sm:hidden">Add 3 people</span>
  <ArrowRight className="ml-2 w-5 h-5" />
</Button>
    
  </Link>
  <div className="flex items-center gap-2 text-[#6D6F7C]">
  <CheckCircle className="w-5 h-5 text-[#81B29A]" />
  <span>Free to start · No pressure · Cancel anytime</span>
</div>
</div>
<p className="text-sm text-[#8a8a9a] mt-4">
  Use it instantly in your browser (and you can add it to your home screen like an app).
</p>
  <p className="text-sm text-[#8a8a9a] mt-4">
  Most people don’t lose touch on purpose. They just get busy — and time quietly passes.
</p>          
          </motion.div>
          
          <motion.div 
            className="lg:col-span-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3206079/pexels-photo-3206079.jpeg" 
                alt="Friends laughing and talking"
                className="rounded-3xl shadow-2xl hero-image-mask w-full"
              />
              {/* Floating stats card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#F4F1EA]"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#81B29A]/20 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#81B29A]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#3D405B]">Stay consistent</p>
                    <p className="text-sm text-[#6D6F7C]">without overthinking it</p>

                  </div>
                </div>
              </motion.div>
              
              {/* Floating streak card */}
              <motion.div 
                className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-[#F4F1EA]"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔥</span>
                  <div>
                    <p className="text-lg font-bold text-[#E07A5F]">12 Week Streak!</p>
                    <p className="text-xs text-[#6D6F7C]">Keep it going</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-4xl md:text-5xl text-[#3D405B] mb-4"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Everything You Need to Stay in Touch
            </h2>
            <p className="text-lg text-[#6D6F7C] max-w-2xl mx-auto">
              Simple, delightful tools designed to help you maintain meaningful relationships
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="card-contact card-lift p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 bg-[#F4F1EA] rounded-2xl flex items-center justify-center mb-6 text-[#E07A5F]">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#3D405B] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                  {feature.title}
                </h3>
                <p className="text-[#6D6F7C] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-6 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-4xl md:text-5xl text-[#3D405B] mb-4"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Simple as 1-2-3
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Add Your People", desc: "Import contacts or add them manually. Set how often you want to call each person." },
              { step: "2", title: "Pick a Rhythm", desc: "Choose how often you want to check in — weekly, monthly, or custom. Adjust anytime." },
              { step: "3", title: "Log It in 10 Seconds", desc: "After you call or text, mark it done and jot a quick note. Future you will thank you." }

            ].map((item, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="w-16 h-16 bg-[#E07A5F] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#3D405B] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#6D6F7C]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-[#3D405B]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
           <h2 
  className="text-4xl md:text-5xl text-white mb-4"
  style={{ fontFamily: 'Fraunces, serif' }}
>
  Built for busy adults
</h2>
<p className="text-white/70 text-lg max-w-2xl mx-auto">
  Early feedback from people testing OuttaTouch during the beta.
</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-3xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="text-white/90 text-lg mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div>
  <p className="text-white/80 text-sm">{testimonial.label}</p>
</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#FDFBF7] relative overflow-hidden">
        <div className="blob-1 -bottom-40 -right-40 opacity-30"></div>
        <motion.div 
          className="max-w-3xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 
            className="text-4xl md:text-5xl text-[#3D405B] mb-6"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Ready to Reconnect?
          </h2>
         <p className="text-lg text-[#6D6F7C] mb-8">
  Start small: add 3 people, pick a rhythm, and do your first check-in this week. It’s free to start.
</p>
          <Link to="/register" data-testid="footer-cta">
            <Button className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-[#F4F1EA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#E07A5F] rounded-full flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>OuttaTouch</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-[#6D6F7C] hover:text-[#E07A5F] text-sm transition-colors" data-testid="footer-pricing-link">
                Pricing
              </Link>
              <Link to="/terms" className="text-[#6D6F7C] hover:text-[#E07A5F] text-sm transition-colors" data-testid="footer-terms-link">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-[#6D6F7C] hover:text-[#E07A5F] text-sm transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center md:text-left">
            <p className="text-[#6D6F7C] text-sm">
              © 2025 MidVantage Labs. Made with ❤️ for meaningful connections.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
